"use client";

import CircularRectTextButton, {
    CircularRectTextButtonProps,
} from "@/components/CircularButton/styled/CircularRectTextButton";
import LazyLoaded from "@/components/LazyLoaded";
import { ASSETS_URL } from "@/config/deployConstants";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { themeColors } from "@/utils/themeColors";
import { AccountType, UpgradeEvent } from "@knowt/syncing/graphql/schema";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { TIME_SECONDS, now } from "@knowt/syncing/utils/dateTimeUtils";
import Popover from "@mui/material/Popover";
import dayjs from "dayjs";
import Image from "next/image";
import { useRouter } from "@bprogress/next";
import { MouseEvent, useCallback, useRef, useState } from "react";
import { toast } from "react-hot-toast";

export const SubscribeRectangularBtn = ({ sx, children, ...btnProps }: CircularRectTextButtonProps) => {
    return (
        <CircularRectTextButton
            sx={{
                backgroundColor: "#FFCD1F",
                color: themeColors.pureWhite,
                borderRadius: "5rem",
                fontWeight: "600",
                fontSize: "1.43rem",
                padding: "1.1rem 1rem",
                ...sx,
            }}
            {...btnProps}>
            {children}
        </CircularRectTextButton>
    );
};

export const UpgradeButton = () => {
    const { areSubsAllowed, user, isTeacher } = useCurrentUser();
    const router = useRouter();
    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);

    if (!user) return null;

    return (
        <SubscribeRectangularBtn
            onClick={() => {
                if (!areSubsAllowed) {
                    return openUpgradePopup({
                        event: UpgradeEvent.REQUEST_UPGRADE,
                        context: {},
                    });
                }

                router.push(isTeacher ? "/plans/teachers" : "/plans");
            }}
            sx={{ width: "14rem", height: "3.8rem", color: themeColors.pureBlack }}>
            Upgrade
        </SubscribeRectangularBtn>
    );
};

export const GamifySubStatusButton = ({
    expiry,
    hoverOnTop,
    style,
}: {
    expiry: number;
    hoverOnTop?: boolean;
    style?: React.CSSProperties;
}) => {
    const { user } = useCurrentUser();

    const loaded = useRef(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const popoverContainerRef = useRef<HTMLDivElement>(null);

    const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
        loaded.current = true;
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = useCallback(
        (e: MouseEvent) => {
            if (!anchorEl) return;

            const currentCursorPosX = e.clientX;
            const currentCursorPosY = e.clientY;

            const popoverMarginTop = 20;

            const isOnPopoverContainer =
                popoverContainerRef.current?.getBoundingClientRect() &&
                currentCursorPosX >= popoverContainerRef.current?.getBoundingClientRect().left &&
                currentCursorPosX <= popoverContainerRef.current?.getBoundingClientRect().right &&
                currentCursorPosY >= popoverContainerRef.current?.getBoundingClientRect().top - popoverMarginTop &&
                currentCursorPosY <= popoverContainerRef.current?.getBoundingClientRect().bottom;

            const isOnAnchorEl =
                anchorEl?.getBoundingClientRect() &&
                currentCursorPosX >= anchorEl?.getBoundingClientRect().left &&
                currentCursorPosX <= anchorEl?.getBoundingClientRect().right &&
                currentCursorPosY >= anchorEl?.getBoundingClientRect().top &&
                currentCursorPosY <= anchorEl?.getBoundingClientRect().bottom;

            if (!isOnPopoverContainer && !isOnAnchorEl) {
                setAnchorEl(null);
            }
        },
        [anchorEl]
    );

    const daysLeft = dayjs.unix(expiry).diff(dayjs(), "days") + 1;

    return (
        <>
            <SubscribeRectangularBtn
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                sx={{
                    width: "20rem",
                    height: "3.8rem",
                    color: anchorEl ? themeColors.mcStudyLight2 : themeColors.mcStudyDark2,
                    backgroundColor: anchorEl ? themeColors.mcStudyDark2 : themeColors.mcStudyLight2,
                    ...style,
                }}>
                {user?.accountType === AccountType.Teacher && now() - (user?.created || 0) < 2 * TIME_SECONDS.WEEK}
                On {daysLeft} day Ultra trial
            </SubscribeRectangularBtn>
            <LazyLoaded load={loaded.current || !!anchorEl}>
                <Popover
                    id="mouse-over-popover"
                    sx={{
                        "& .MuiPopover-paper": {
                            backgroundColor: themeColors.neutralBlack,
                            padding: "1rem",
                            color: themeColors.neutralWhite,
                            borderRadius: "0.6rem",
                            boxShadow: "none",
                            marginTop: hoverOnTop ? "-1rem" : "2rem",
                            maxWidth: "27rem",
                        },
                    }}
                    onMouseMove={handlePopoverClose}
                    slotProps={{
                        paper: {
                            ref: popoverContainerRef,
                        },
                    }}
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: hoverOnTop ? "top" : "bottom",
                        horizontal: hoverOnTop ? "center" : "right",
                    }}
                    transformOrigin={{
                        vertical: hoverOnTop ? "bottom" : "top",
                        horizontal: hoverOnTop ? "center" : "right",
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                    disableScrollLock>
                    <span
                        className="secondaryText1"
                        style={{
                            color: themeColors.neutralWhite,
                        }}>
                        Your{" "}
                        {now() - (user?.created || 0) < 2 * TIME_SECONDS.WEEK ? "trial" : "store bought subscription"}{" "}
                        will end automatically in {daysLeft} days, but you’ll still have access to our free basic
                        plan—no action required after. If you’re happy already and want to upgrade, click this button
                        anytime!
                    </span>
                </Popover>
            </LazyLoaded>
        </>
    );
};

export const RemoveAds = () => {
    const { isEnforcedDPA, user, isTeacher } = useCurrentUser();
    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);

    if (!user) return null;

    return (
        <SubscribeRectangularBtn
            onClick={() => {
                Mixpanel.track("Permanent Upgrade Clicked", {
                    isEnforcedDPA,
                    isTeacher,
                });

                return openUpgradePopup({
                    event: UpgradeEvent.REMOVE_ADS,
                    context: {},
                });
            }}
            sx={{ width: "14rem", height: "3.8rem", color: themeColors.pureBlack }}>
            Remove ads
        </SubscribeRectangularBtn>
    );
};

export const ResubscribeButton = () => {
    const { resubscribe } = useCurrentUser();

    return (
        <SubscribeRectangularBtn
            sx={{ width: "16rem", height: "3.8rem" }}
            onClick={async () => {
                await toast.promise(resubscribe(), {
                    loading: "Restarting your subscription...",
                    success: "Success!",
                    error: "Please try again later.",
                });
            }}>
            Resubscribe
            <Image
                src={`${ASSETS_URL}/images/raised_hands.png`}
                alt={"hands"}
                style={{ marginBottom: "0.3rem", objectFit: "cover" }}
                height={32}
                width={32}
            />
        </SubscribeRectangularBtn>
    );
};
